/*
 * safety-cover-blocks.js
 * Engine for the block-built "Build my cover" section.
 *
 * WHY THIS EXISTS
 * ---------------
 * The first version of this builder (sections/safety-cover-builder.liquid) owned its
 * own markup, so every heading, colour and card in it was frozen in Liquid. This one
 * owns no markup at all. The section is an ordinary Flexible columns section built out
 * of theme blocks, and this file finds the parts it has to drive by reading small
 * <sc-part> markers that live inside Custom Liquid blocks.
 *
 * CONTRACT WITH THE SECTION
 * -------------------------
 *   <sc-part part="step" id="fab" key="fab" option="fabric"
 *            requires="mode=new" property="Order type" helper="...">
 *       Placed in any block inside a Row. That Row becomes a question step.
 *         id        unique name for the step
 *         key       state key the step writes
 *         option    variant option this key resolves against: fabric | spacing | size
 *         property  line item property name, for answers that are not variant options
 *         summary   what this question is called in the quote form's summary of the
 *                   build. Defaults to property, then to the question's own heading.
 *         requires  comma separated predicates, "key=value" or "key!=value"
 *         helper    guidance shown in [sc:helper] while this step is the open one
 *
 *   <sc-part part="choice" value="Royal Mesh" label="Royal Mesh">
 *       Placed in any block inside a Column. That Column becomes a clickable answer,
 *       wearing a radio down its side so the question reads as the pick-one it is. The
 *       row those answers sit in becomes a radio group named by its own heading: one
 *       tab stop, arrow keys between the answers. An answer with action="scroll" is an
 *       exit rather than an answer: it keeps the radio but stays a plain button, outside
 *       the group, and is never checked.
 *         value     the answer. For option-backed steps this is the option value.
 *         label     what the answer is called. Defaults to whatever the column leads
 *                   with, a heading or bold text.
 *         action    "scroll" makes it an exit that changes no state. It wears the radio
 *                   like any other option but is never the checked one.
 *         target    selector to scroll to, for action="scroll"
 *         control   "size" fills the column with the size dropdown
 *         addon     "cutout" sells the with-cutout product instead of the plain one
 *         disqualify  this answer cannot be sold from the page.
 *                     "auto"  route by who the shopper is: a replacement buyer goes to
 *                             the Perfect Fit Box, everybody else to a quote
 *                     "quote" always the quote form
 *                     "box"   always the Perfect Fit Box
 *         quote     deprecated alias for disqualify="quote"
 *
 * THREE EXITS
 * -----------
 * Every shopper answers the same questions. What changes is where the last one lands:
 *
 *   instant   every answer resolves to a variant. Add to cart.
 *   quote     something needs pricing by hand. The contact form opens in place.
 *   box       the same, for somebody who already owns a cover we can template from.
 *
 * That is one rule, and it lives in path(). A disqualifying answer routes to `box` when
 * the engine's data-box-when predicate holds (by default mode=repl) and to `quote`
 * otherwise. The reason it is one rule rather than a list of triggers: every disqualifier
 * we have -- an unlisted size, a swimout, uneven anchors, and the shape and deck questions
 * when they land -- is solved by templating the old cover if there is one. Which exit a
 * shopper takes is a fact about the shopper, not about which question tripped.
 *
 * The result is exposed as the derived state key `path`, so a step or a field can gate on
 * it with requires="path=quote" exactly like any answer. It is derived, never answered, so
 * it is not a question and never appears in the counter.
 *
 *   <sc-part part="when" requires="path=instant">
 *       Placed in any block inside a Column. That Column is shown only while the
 *       predicates hold, in the same "key=value" / "key!=value" form a step's requires=
 *       uses -- so it reads any answer, and `path` in particular. Content that belongs to
 *       one route and not another: the refund terms, which are true of a cover you are
 *       buying and meaningless next to a quote request.
 *         requires  comma separated predicates, same grammar as a step's
 *
 *   [sc:name] typed into any heading, caption or text block is replaced with a live
 *   value. See TOKEN NAMES in setTokens().
 *
 *   A button whose link is #sc-add submits the cart form. #sc-quote is the quote CTA.
 *
 * TWO PRODUCTS
 * ------------
 * The step cutout is not an add-on line. It is a second cover product with the same 130
 * option combinations at a higher price, and an answer carrying addon="cutout" switches
 * which of the two is being sold. Both variant lists arrive in the page; this file pairs
 * them up by (fabric, spacing, size) and resolves one variant of one product.
 *
 * That replaced a two-line cart, a shared _cover_group property on both lines, and a
 * site-wide guard that swept the cart for a cutout whose cover had been removed. One
 * line item cannot be half-removed, so none of it is needed any more.
 *
 * PRICES
 * ------
 * Never computed here. Every price string arrives pre-formatted by the Liquid `money`
 * filter in the variant blob. If a price is not in the blob there is no price to show.
 */
(function () {
  'use strict';

  var ROW = '.block-grid-item__flex-wrapper';
  var COL = '.block-grid-item';
  var FABRIC_KEYS = ['royal', 'king', 'emperor'];
  // Breathing room under a bottom-resting pinned column, so its last line is not welded
  // to the bottom edge of the screen. See THE PINNED COLUMN.
  var PIN_GAP = 24;

  function norm(v) {
    return (v == null ? '' : String(v)).trim().toLowerCase();
  }

  // "Royal Mesh" -> "royal". Substring matching so renaming an option value in admin
  // ("King99 Mesh" -> "King 99 Mesh") does not break the section.
  function fabricKey(value) {
    var v = norm(value);
    for (var i = 0; i < FABRIC_KEYS.length; i++) {
      if (v.indexOf(FABRIC_KEYS[i]) !== -1) return FABRIC_KEYS[i];
    }
    return v.replace(/[^a-z0-9]/g, '');
  }

  // "12' x 24'" -> [12, 24].  "5x5" -> [5, 5].
  function dims(value) {
    var m = norm(value).match(/\d+(?:\.\d+)?/g);
    if (!m || m.length < 2) return null;
    return [parseFloat(m[0]), parseFloat(m[1])];
  }

  function dimKey(value) {
    var d = dims(value);
    return d ? d[0] + 'x' + d[1] : norm(value).replace(/[^a-z0-9]/g, '');
  }

  function attr(el, name) {
    var v = el.getAttribute(name);
    return v == null || v === '' ? null : v;
  }

  function warn(msg) {
    if (window.console && console.warn) console.warn('[safety-cover] ' + msg);
  }

  // Only used when a choice marker carries no label of its own. An answer's label is
  // whatever it leads with, which may be a heading or, as on this page, bold text.
  function headingText(el) {
    var h = el.querySelector('h1, h2, h3, h4, h5, h6, strong, b');
    return h ? h.textContent.trim() : el.textContent.trim().split('\n')[0];
  }

  /* ------------------------------------------------------------------ */

  function Builder(engine) {
    this.engine = engine;
    this.root = engine.closest('.shopify-section') || engine.parentNode;
    this.cfg = engine.dataset;
    this.designMode = !!(window.Shopify && window.Shopify.designMode);

    var blob = engine.querySelector('[data-sc-variants]');
    if (!blob) throw new Error('no variant data in the engine block.');
    this.variants = JSON.parse(blob.textContent);
    if (!this.variants || !this.variants.length) {
      throw new Error('the variant list is empty. Check the product is Active and published to Online Store.');
    }

    /* The with-cutout product. Absent is a legitimate state -- it means no answer can
       switch to it -- so this is not an error, it is an empty index. What must never
       happen is the plain cover being sold at the cutout price because its twin was
       missing, and an empty index is what prevents that: the lookup finds nothing and
       the answer routes to quote.

       Legitimate, but almost never intended, and it used to be silent. The diagnosis is
       in checkTwin(), which runs once the markers have been read so it can say whether
       any answer actually needed the twin. */
    var addonBlob = engine.querySelector('[data-sc-addon-variants]');
    this.addonVariants = addonBlob ? JSON.parse(addonBlob.textContent) || [] : [];

    this.state = {};
    this.labels = {};
    this.openAll = false;
    this.forcedStep = null;

    this.indexVariants();
    this.collectParts();
    if (!this.steps.length) {
      throw new Error('no <sc-part part="step"> markers found in this section. Nothing to drive.');
    }
    this.checkTwin();
    this.collectTokens();
    this.setupForm();
    this.bind();
    this.restoreFromUrl();
    this.render();
    this.root.classList.add('sc-ready');

    /* Shopify posts the contact form as a whole page load, so a shopper who just sent a
       quote request comes back to an empty builder with their confirmation buried in a
       question they can no longer see. Put them in front of it. */
    if (this.posted && this.quotePanel) {
      var panel = this.quotePanel;
      requestAnimationFrame(function () {
        panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  Builder.prototype = {
    /* fabric -> spacing -> size, straight off the blobs. A combination that is not
       manufactured is simply absent; there is no second availability list.

       Both products are indexed the same way, which is the whole pairing mechanism:
       the two share option VALUES, so the same three keys address the plain cover and
       its with-cutout twin. Nothing has to be told that variant 123 goes with 456. */
    indexVariants: function () {
      this.index = {};
      this.addonIndex = {};
      this.sizeOrder = [];
      this.byId = {};
      this.fill(this.index, this.variants, true);
      this.fill(this.addonIndex, this.addonVariants, false);
    },

    // sizeOrder comes off the plain cover only. It is the order the dropdown reads in,
    // and the twin is the same 130 combinations, so taking it from both would just
    // append duplicates.
    fill: function (index, list, ordered) {
      for (var i = 0; i < list.length; i++) {
        var v = list[i];
        v.fabKey = fabricKey(v.fabric);
        v.gridKey = dimKey(v.spacing);
        v.sizeKey = dimKey(v.size);
        if (!index[v.fabKey]) index[v.fabKey] = {};
        if (!index[v.fabKey][v.gridKey]) index[v.fabKey][v.gridKey] = {};
        index[v.fabKey][v.gridKey][v.sizeKey] = v;
        if (ordered && this.sizeOrder.indexOf(v.sizeKey) === -1) this.sizeOrder.push(v.sizeKey);
        this.byId[String(v.id)] = v;
      }
    },

    /* WHY THIS EXISTS AT ALL.

       A missing twin has no symptom of its own. Every answer carrying addon="cutout"
       resolves to nothing, `sellable()` returns null, and the panel shows "Get a Quote"
       -- which is exactly what a combination that is not manufactured looks like. So the
       page appears to be refusing to sell a perfectly ordinary rectangle, and nothing
       anywhere says why.

       It used to be worse: the only warning fired when the blob was present but EMPTY,
       and the likeliest cause -- a handle that does not match, or a product that is not
       published to the Online Store sales channel -- makes the Liquid skip the blob
       entirely. The most probable failure produced no output at all.

       Said once, at boot, and only when an answer actually needs the twin: a section
       with no cutout answers in it is not misconfigured. */
    checkTwin: function () {
      if (this.addonVariants.length) return;

      var wanted = 0;
      for (var i = 0; i < this.steps.length; i++) {
        for (var j = 0; j < this.steps[i].choices.length; j++) {
          if (this.steps[i].choices[j].addon) wanted++;
        }
      }
      if (!wanted) return;

      var handle = this.cfg.addonMissing;
      var tail = ' Until it resolves, all ' + wanted + ' "steps on the end" answers show the ' +
        'quote CTA instead of a price. That is the safe direction -- the alternative is ' +
        'selling a plain cover at the cutout price -- but it is not what a live page should do.';

      if (!handle) {
        warn('the with-cutout product rendered an empty variant list.' + tail);
        return;
      }
      if (this.cfg.addonFault === 'no-variants') {
        warn('the with-cutout product "' + handle + '" was found but the storefront can see ' +
          'no variants on it.' + tail);
        return;
      }
      warn('the with-cutout product "' + handle + '" is not on the storefront. Either that ' +
        'handle is wrong, or the product is not published to the Online Store sales channel ' +
        '-- publishing is a separate control from the Active / Draft status, so a product ' +
        'can be Active and still be invisible here. Check the handle under Search engine ' +
        'listing, and the Publishing panel for the channel.' + tail);
    },

    /* --------------------------- markers --------------------------- */

    collectParts: function () {
      var markers = this.root.querySelectorAll('sc-part');
      var pending = [];
      var i;

      this.steps = [];
      this.keyOrder = [];
      this.conditionals = [];

      for (i = 0; i < markers.length; i++) {
        var m = markers[i];
        var role = attr(m, 'part');

        var owner = role === 'step' ? m.closest(ROW) || m.closest(COL) : m.closest(COL);
        // A "when" column that wraps the whole panel would hide the panel. Almost always
        // a marker dropped one level too high, and silently blanking the price is worse
        // than ignoring it.
        if (role === 'when' && owner && owner.querySelector('[data-sc-cart], [data-sc-quote-panel]')) {
          warn('a <sc-part part="when"> column also contains the cart or the quote form. ' +
            'It would hide them. Put it on a column of its own. Ignored.');
          this.retireMarker(m);
          continue;
        }
        if (!owner) {
          warn('a <sc-part part="' + role + '"> is not inside a Row or Column. Ignored.');
          this.retireMarker(m);
          continue;
        }

        if (role === 'step') {
          var step = {
            el: owner,
            id: attr(m, 'id') || 'step-' + (this.steps.length + 1),
            key: attr(m, 'key'),
            option: attr(m, 'option'),
            property: attr(m, 'property'),
            // What this question is called in the quote form's summary of what the
            // shopper picked. Falls back to property, then the question's own heading.
            summary: attr(m, 'summary'),
            requires: attr(m, 'requires'),
            helper: attr(m, 'helper'),
            choices: []
          };
          owner.classList.add('sc-step');
          owner.setAttribute('data-sc-step', step.id);
          this.steps.push(step);
          if (step.key && this.keyOrder.indexOf(step.key) === -1) this.keyOrder.push(step.key);
        } else if (role === 'when') {
          owner.setAttribute('data-sc-when', attr(m, 'requires') || '');
          this.conditionals.push({ el: owner, requires: attr(m, 'requires') });
        } else if (role === 'choice') {
          pending.push({
            el: owner,
            value: attr(m, 'value'),
            label: attr(m, 'label'),
            action: attr(m, 'action'),
            target: attr(m, 'target'),
            control: attr(m, 'control'),
            addon: attr(m, 'addon'),
            // quote="true" is the old spelling of disqualify="quote". Kept working
            // because it is in the live template; new markers should use disqualify.
            disqualify: attr(m, 'disqualify') || (attr(m, 'quote') ? 'quote' : null)
          });
        } else {
          warn('unknown <sc-part part="' + role + '">. Ignored.');
        }
        this.retireMarker(m);
      }

      // Which step does each choice belong to: the innermost step row containing it.
      for (i = 0; i < pending.length; i++) {
        var c = pending[i];
        var owning = null;
        for (var j = 0; j < this.steps.length; j++) {
          if (this.steps[j].el.contains(c.el)) owning = this.steps[j];
        }
        if (!owning) {
          warn('a choice column is not inside a step row. Ignored.');
          continue;
        }
        this.attachChoice(owning, c);
      }

      this.labelGroups();

      // Which state key answers which variant option.
      this.roleKey = {};
      for (i = 0; i < this.steps.length; i++) {
        if (this.steps[i].option) this.roleKey[this.steps[i].option] = this.steps[i].key;
      }

    },

    // A marker is scaffolding, not content, so it is retired rather than deleted: the
    // part name is suffixed, which stops the pre-hide stylesheet rule matching it, and CSS
    // keeps it invisible. It has to stay in the document so the theme editor can still
    // select it and so a select event can be traced back to the step it belongs to.
    // The Custom Liquid wrapper it came in is hidden if it holds nothing else.
    retireMarker: function (m) {
      m.setAttribute('part', (m.getAttribute('part') || 'sc') + '-bound');
      var wrap = m.parentNode;
      if (!wrap || wrap.nodeType !== 1 || wrap.textContent.trim()) return;
      for (var i = 0; i < wrap.children.length; i++) {
        if (wrap.children[i].tagName !== 'SC-PART') return;
      }
      wrap.classList.add('hidden');
    },

    attachChoice: function (step, c) {
      c.step = step;
      step.choices.push(c);
      c.el.classList.add('sc-choice');
      c.el.setAttribute('data-sc-choice', step.id);

      if (c.control === 'size') {
        c.el.classList.add('sc-choice--control');
        this.mountSizeControl(c);
        return;
      }

      if (!c.label) c.label = headingText(c.el);
      c.norm = this.normalise(step, c.value);

      // An exit changes no state, so it stays an ordinary button and is not part of the
      // group: it can never be the checked one, because choosing it navigates instead of
      // answering. It still wears the radio, because to a shopper it is one of the
      // options in the question and an option with nothing beside it reads as not
      // pickable. The radio is a pseudo-element with no semantics of its own, so what a
      // screen reader hears is still "button", which is what it does.
      if (c.action === 'scroll') {
        c.el.setAttribute('role', 'button');
        c.el.setAttribute('tabindex', '0');
        c.el.classList.add('sc-choice--radio');
        return;
      }
      // paintChoices owns tabindex from here on -- a radio group is one tab stop, not
      // one per answer.
      c.el.classList.add('sc-choice--radio');
      c.el.setAttribute('role', 'radio');
      c.el.setAttribute('aria-checked', 'false');
      c.el.setAttribute('tabindex', '-1');
    },

    // The size answers are 23 pool sizes filtered by what the chosen fabric and spacing
    // can actually be built in, so they cannot be laid out as blocks. The dropdown
    // markup comes from the engine's <template> so it carries the theme's own select
    // styling and caret.
    mountSizeControl: function (c) {
      var tpl = this.engine.querySelector('[data-sc-size-template]');
      var host = c.el.querySelector('.block-grid-item__inner') || c.el;
      if (!tpl) {
        warn('no size dropdown template in the engine block.');
        return;
      }
      host.appendChild(tpl.content.cloneNode(true));
      this.sizeSelect = host.querySelector('[data-sc-size]');
      c.el.removeAttribute('role');
    },

    // A question with radios in it is a radio group, and its own heading is what names
    // the group. The heading is given an id only if it has not got one, so a merchandiser
    // who set one in the editor keeps it. A question whose only answer is the size
    // dropdown is not a group -- the <select> is already the control.
    labelGroups: function () {
      for (var i = 0; i < this.steps.length; i++) {
        var step = this.steps[i];
        if (!this.radios(step).length) continue;
        step.el.setAttribute('role', 'radiogroup');
        var h = step.el.querySelector('h1, h2, h3, h4, h5, h6');
        if (!h) continue;
        if (!h.id) h.id = 'sc-q-' + step.id;
        step.el.setAttribute('aria-labelledby', h.id);
      }
    },

    // The answers to a question that belong to its radio group, in the order they sit on
    // the page. Exits and the size dropdown are in the row but not in the group.
    radios: function (step) {
      var list = [];
      for (var i = 0; i < step.choices.length; i++) {
        var c = step.choices[i];
        if (c.control !== 'size' && c.action !== 'scroll') list.push(c);
      }
      return list;
    },

    normalise: function (step, value) {
      if (step.option === 'fabric') return fabricKey(value);
      if (step.option === 'spacing' || step.option === 'size') return dimKey(value);
      return norm(value);
    },

    /* ---------------------------- tokens ---------------------------- */

    // [sc:price] typed anywhere in the section becomes a span this file writes into.
    // Done once, on init. Heading blocks pass brackets through untouched as long as
    // there is no pipe in them, which there never is here.
    collectTokens: function () {
      this.tokens = [];
      var walker = document.createTreeWalker(this.root, NodeFilter.SHOW_TEXT, null, false);
      var nodes = [];
      var n;
      while ((n = walker.nextNode())) {
        if (n.nodeValue.indexOf('[sc:') === -1) continue;
        var tag = n.parentNode && n.parentNode.nodeName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEXTAREA') continue;
        nodes.push(n);
      }

      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        var text = node.nodeValue;
        var re = /\[sc:([a-z][a-z0-9-]*)\]/g;
        var frag = document.createDocumentFragment();
        var last = 0;
        var m;
        while ((m = re.exec(text))) {
          if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
          var span = document.createElement('span');
          span.setAttribute('data-sc-token', m[1]);
          frag.appendChild(span);
          this.tokens.push(span);
          last = re.lastIndex;
        }
        if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
        node.parentNode.replaceChild(frag, node);
      }
    },

    /* ----------------------------- form ----------------------------- */

    // The cart form is its own Custom Liquid block, so it sits wherever the merchandiser
    // put it in the layout rather than inside the engine block.
    setupForm: function () {
      var host = this.root.querySelector('[data-sc-cart]') || this.engine;
      this.form = host.querySelector('form');
      this.nativeSelect = host.querySelector('[data-sc-native]');
      this.fallback = host.querySelectorAll('[data-sc-fallback]');
      this.terms = host.querySelector('[data-sc-terms]');
      this.sticky = host.querySelector('[data-sc-sticky]');
      if (!this.form) warn('no cart form on the page. Add the engine block with part: "form".');

      // The native picker was the input until this ran. Now the builder is.
      for (var i = 0; i < this.fallback.length; i++) this.fallback[i].classList.add('hidden');

      this.addCta = this.root.querySelector('[href*="#sc-add"]');
      this.quoteCta = this.root.querySelector('[href*="#sc-quote"]');
      if (!this.addCta) warn('no button links to #sc-add, so nothing adds to the cart.');

      /* The quote form, if the section carries one. Without it the engine keeps its old
         behaviour and scrolls to data-quote-target, so a section that has not been
         rebuilt yet still works. */
      this.quotePanel = this.root.querySelector('[data-sc-quote-panel]');
      /* The sticky column the price, the form and the quote panel sit in, if it is one.
         Escaped because the theme's class is literally "md:sticky". Absent is fine -- a
         merchandiser can untick "Stick when scrolling on desktop", and then the page
         scroll reaches everything already and there is nothing to drive.

         Read off the cart block rather than the quote panel, because the column can
         outgrow the screen with nothing in it but the price box and the help card on a
         short laptop. The quote form is only the loudest way it happens. */
      var pinnedIn = (host !== this.engine ? host : null) || this.quotePanel;
      this.stickyColumn = pinnedIn
        ? pinnedIn.closest('.block-grid-item.md\\:sticky')
        : null;
      this.posted = !!(this.quotePanel && this.quotePanel.querySelector('[data-sc-posted]'));
      if (!this.quotePanel && !this.cfg.quoteTarget) {
        warn('no quote panel and no data-quote-target. An answer that cannot be sold has nowhere to go.');
      }
    },

    bind: function () {
      var self = this;

      this.root.addEventListener('click', function (evt) {
        var choiceEl = evt.target.closest('[data-sc-choice]');
        if (choiceEl && self.root.contains(choiceEl)) {
          var c = self.findChoice(choiceEl);
          if (c && c.control !== 'size') {
            evt.preventDefault();
            self.answer(c);
            return;
          }
        }
        var cta = evt.target.closest('[href*="#sc-add"], [href*="#sc-quote"]');
        if (!cta || !self.root.contains(cta)) return;
        evt.preventDefault();
        if (cta === self.quoteCta) self.showQuote();
        else self.submit();
      });

      this.root.addEventListener('keydown', function (evt) {
        var choiceEl = evt.target.closest && evt.target.closest('[data-sc-choice]');
        if (!choiceEl || !self.root.contains(choiceEl)) return;
        var c = self.findChoice(choiceEl);
        if (!c || c.control === 'size') return;

        if (evt.key === 'Enter' || evt.key === ' ') {
          evt.preventDefault();
          self.answer(c);
          return;
        }

        // Arrow keys move between the answers to one question and choose as they go,
        // which is what a radio group does everywhere else. Exits are not in the group.
        if (c.action === 'scroll') return;
        var dir = evt.key === 'ArrowDown' || evt.key === 'ArrowRight' ? 1
          : evt.key === 'ArrowUp' || evt.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        var next = self.nextRadio(c, dir);
        if (!next) return;
        evt.preventDefault();
        // Answering normally scrolls the next question into view. Not while somebody is
        // arrowing through this one -- the page would run away from the keyboard.
        self.keyMove = true;
        self.answer(next);
        self.keyMove = false;
        next.el.focus();
      });

      if (this.sizeSelect) {
        this.sizeSelect.addEventListener('change', function () {
          var sizeKey = self.roleKey.size;
          self.state[sizeKey] = this.value || null;
          self.labels[sizeKey] = this.selectedOptions[0] ? this.selectedOptions[0].dataset.label || '' : '';
          self.render();
        });
      }

      // Theme editor only. Selecting any block inside a question opens that question so
      // it can be edited, then closes it again on deselect.
      if (this.designMode) {
        document.addEventListener('shopify:block:select', function (evt) {
          var el = evt.target;
          var step = el && el.closest ? el.closest('.sc-step') : null;
          self.forcedStep = step ? step.getAttribute('data-sc-step') : null;
          self.render();
          if (step) step.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        document.addEventListener('shopify:block:deselect', function () {
          self.forcedStep = null;
          self.render();
        });
      }

      var stickyAdd = this.sticky && this.sticky.querySelector('[data-sc-sticky-add]');
      if (stickyAdd) {
        stickyAdd.addEventListener('click', function () {
          self.submit();
        });
      }

      this.watchPinnedColumn();
    },

    /* --------------------------- THE PINNED COLUMN ---------------------------

       The summary column is sticky, and sticky pins an element's TOP. Whatever does not
       fit on the screen stays off the screen however far the shopper scrolls, which is
       fine for a price and useless for the quote form, whose own submit button ends up
       out of reach. The column outgrows the viewport for ordinary reasons too: five
       answered questions, a price, a form and a help card on a laptop screen.

       Two fixes have been tried and both were worse than the bug.

         position: static while the form was open. That returned the column to its place
         in the document -- the top of the section -- so a shopper who answered the last
         question near the bottom of the page watched the panel they had just opened shoot
         up out of the viewport, leaving the blank space it had been floating over.

         max-height + overflow-y: auto. That put a second scrollbar inside the page's own
         and asked the shopper to find it. Nothing on a storefront should scroll inside
         itself like that.

       What happens now is what a well-behaved tall sidebar does anywhere: while the column
       is taller than the screen it TRAVELS with the page rather than being pinned to one
       edge of it. Scroll down and it slides up with the content until its bottom rests
       just above the fold -- so the submit button is what is waiting at the end of the
       scroll -- and stops. Scroll back up and it slides down until its head is under the
       header, and stops there. Both ends are reachable, nothing is clipped, and because
       the column never leaves flow and its `top` only ever moves by exactly as much as the
       page just scrolled, nothing can jump.

       All of that is one number: the `top` sticky is pinning against, rewritten per scroll
       frame between two bounds. While the column fits on screen the number is not written
       at all and the theme's own rule stands untouched. */

    watchPinnedColumn: function () {
      var col = this.stickyColumn;
      if (!col) return;
      var self = this;
      this.pin = { top: null, y: window.pageYOffset || 0, frame: 0 };

      var nudge = function () { self.schedulePin(); };
      window.addEventListener('scroll', nudge, { passive: true });
      window.addEventListener('resize', nudge, { passive: true });

      /* The column changes height for reasons that are not scrolling: the quote form
         opening, a question folding away, a late image. Each one moves the bound the
         column comes to rest against. */
      if (window.ResizeObserver) {
        this.pinResize = new window.ResizeObserver(nudge);
        this.pinResize.observe(col);
      }
      this.followPin();
    },

    // One update per frame, however many scroll events arrive inside it.
    schedulePin: function () {
      var self = this;
      if (!this.pin || this.pin.frame) return;
      if (!window.requestAnimationFrame) { this.followPin(); return; }
      this.pin.frame = window.requestAnimationFrame(function () {
        self.pin.frame = 0;
        self.followPin();
      });
    },

    followPin: function () {
      var col = this.stickyColumn;
      var pin = this.pin;
      if (!col || !pin) return;

      var y = window.pageYOffset || 0;
      var dy = y - pin.y;
      pin.y = y;

      /* Asked of the element rather than of a breakpoint, because it is the same question
         asked properly: below 750px the theme leaves this column static, and a
         merchandiser can untick "Stick when scrolling on desktop" at any width. Either
         way it is in normal flow, the page scroll already reaches all of it, and there is
         nothing here to drive. */
      var style = window.getComputedStyle(col);
      if (String(style.position).indexOf('sticky') === -1) return this.releasePin();

      // The theme's own resting place: under the sticky header, at whatever height it is
      // right now -- it shrinks as the header hides itself on the way down the page.
      var head = parseFloat(style.getPropertyValue('--header-height-live')) || 0;
      var height = col.getBoundingClientRect().height || col.offsetHeight || 0;
      // Where the column comes to rest on the way down: last line just above the fold.
      var floor = window.innerHeight - height - PIN_GAP;
      // It fits. Nothing to travel, and the stylesheet's `top` is the right one.
      if (floor >= head) return this.releasePin();

      if (pin.top === null) {
        pin.top = head;
        col.classList.add('sc-follow');
      }

      /* Only a column that is actually pinned travels. One still sitting in flow -- the
         shopper is above it, scrolling down towards it -- keeps the header offset, which
         is where it should come to rest the moment it does pin. */
      if (col.getBoundingClientRect().top > pin.top + 1) pin.top = head;
      // Moving by exactly the scroll delta is what makes the travel read as ordinary page
      // scrolling. The two bounds are what turn it back into sticky at either end.
      else pin.top = pin.top - dy;
      pin.top = Math.max(floor, Math.min(head, pin.top));

      col.style.top = Math.round(pin.top) + 'px';
    },

    /* OPENING THE FORM IS A CHANGE OF PLACE.

       The panel opens in the summary column, and by the time a shopper has answered the
       last question that column is usually not where they are looking. It runs out of
       section before the questions do, so it comes to rest against the bottom of the grid
       and is carried up off the top of the screen; or it is mid-travel, resting with its
       foot above the fold. Either way the form opens somewhere the shopper then has to go
       and find, which is what they were doing before this existed.

       So the page goes back to it: one smooth scroll that puts the head of the column
       under the header, where the "by quote" line, the form's heading and its first
       fields are. Only when it is actually out of the way -- above the header line, or
       (stacked, below 750px) entirely below the fold. A column already on screen does not
       move, because nothing needs to.

       The distance is the column's, not the page's. Scrolling up by D moves the column
       down by exactly D whether it is pinned and travelling, held against the bottom of
       the grid, or in normal flow, so `top - head` is the whole calculation, and the
       travel's own clamp is what stops the column under the header rather than past it. */
    revealColumn: function () {
      var col = this.stickyColumn;
      // Never in the theme editor, and never mid-arrow-key: both move the page for their
      // own reasons and a second scroll on top of that is a page running away.
      if (!col || !this.quotePanel || this.designMode || this.keyMove) return;

      var head = parseFloat(
        window.getComputedStyle(col).getPropertyValue('--header-height-live')) || 0;
      var top = col.getBoundingClientRect().top;
      var lost = top < head ||
        this.quotePanel.getBoundingClientRect().top >= (window.innerHeight || 0);
      if (!lost) return;

      var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (window.scrollBy) {
        window.scrollBy({ top: top - head, left: 0, behavior: still ? 'auto' : 'smooth' });
      }

      /* Somebody on a keyboard has to be taken with the page, or their next Tab carries
         on from a question that is now off screen. The panel rather than a field inside
         it: this scroll was not asked for by a click on the form, and dropping a caret
         into a text input nobody chose pops the keyboard open on a phone. */
      this.quotePanel.setAttribute('tabindex', '-1');
      this.quotePanel.focus({ preventScroll: true });
    },

    // Hand the column back to the stylesheet, whole.
    releasePin: function () {
      if (!this.pin || this.pin.top === null) return;
      this.pin.top = null;
      this.stickyColumn.style.top = '';
      this.stickyColumn.classList.remove('sc-follow');
    },

    // The next or previous answer in the same question, wrapping at both ends.
    nextRadio: function (c, dir) {
      var group = this.radios(c.step);
      var at = group.indexOf(c);
      if (at === -1 || group.length < 2) return null;
      return group[(at + dir + group.length) % group.length];
    },

    findChoice: function (el) {
      for (var i = 0; i < this.steps.length; i++) {
        for (var j = 0; j < this.steps[i].choices.length; j++) {
          if (this.steps[i].choices[j].el === el) return this.steps[i].choices[j];
        }
      }
      return null;
    },

    submit: function () {
      this.addToCart();
    },
    /* ------------------------------ the cart ------------------------------ */

    /* One line item, whichever of the two products it belongs to.

       The request is still ours rather than the theme's <product-form>, because that
       form is bound to the plain cover and posts FormData built from the markup inside
       it -- it cannot post a variant of the other product. What is reused is everything
       after the request: the same section list, the same renderContents calls on the
       drawer / cart / notification, and the same cartUpdate event, so the cart behaves
       exactly as it does anywhere else on the store.

       This used to post two lines, a cover and a cutout, tied together by a shared
       _cover_group property and watched over by a cart-wide guard. Selling the cutout as
       a second cover product retired all of that: one line cannot be half-removed, and
       the price is Shopify's rather than something the page reconstructed. */
    addToCart: function () {
      if (this.addDisabled || this.busy) return;
      var variant = this.sellable();
      if (!variant) return;

      var self = this;
      var items = [{ id: variant.id, quantity: 1, properties: this.lineProperties() }];

      var cart = window.SafetyCoverCart;
      if (!cart) {
        warn('safety-cover-cart.js is not loaded, so nothing can be added to the cart.');
        return;
      }
      var sections = cart.sections();
      var body = { items: items };
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      var drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.setActiveElement && window.theme &&
          theme.settings.cartDrawer && theme.settings.cartDrawer.atcAction === 'drawer') {
        drawer.setActiveElement(document.activeElement);
      }

      this.busy = true;
      this.setBusy(true);
      fetch((window.routes && window.routes.cart_add_url) || cart.root() + 'cart/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) {
            self.showError(data.description || data.message);
            return;
          }
          self.showError('');
          if (window.theme && theme.settings.cartDrawer && theme.settings.cartDrawer.atcAction === 'page') {
            window.location = window.routes.cart_url;
            return;
          }
          cart.render(data);
          if (window.publish && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'safety-cover-builder',
              productVariantId: variant.id,
              cartData: data
            });
          }
        })
        .catch(function (e) {
          self.showError(e.message);
        })
        .finally(function () {
          self.busy = false;
          self.setBusy(false);
        });
    },

    // Answers that are not variant options ride along as line item properties.
    lineProperties: function () {
      var props = {};
      for (var i = 0; i < this.steps.length; i++) {
        var step = this.steps[i];
        if (!step.property || !this.eligible(step) || !this.answered(step)) continue;
        props[step.property] = this.labels[step.key] || '';
      }
      return props;
    },

    // The theme's own error slot, already in the form markup.
    showError: function (message) {
      var wrap = this.root.querySelector('.product-form__error-message-wrapper');
      if (!wrap) return;
      var slot = wrap.querySelector('.product-form__error-message');
      if (slot) slot.textContent = message || '';
      if (message) wrap.removeAttribute('hidden');
      else wrap.setAttribute('hidden', '');
    },

    setBusy: function (on) {
      if (this.addCta) this.addCta.classList.toggle('loading', on);
      var stickyAdd = this.sticky && this.sticky.querySelector('[data-sc-sticky-add]');
      if (stickyAdd) stickyAdd.disabled = on;
    },

    /* Does the answer currently chosen mean the with-cutout product?

       Only ever on the instant path. Off it there is no variant being sold, so pointing
       the lookup at the twin index would only make the panel read "by quote" for a second
       reason it is already reading it for. */
    addonWanted: function () {
      if (this.state.path !== 'instant') return false;
      var c = this.selectedChoice();
      return !!(c && c.addon);
    },

    selectedChoice: function () {
      for (var i = 0; i < this.steps.length; i++) {
        var step = this.steps[i];
        if (!step.key || !this.eligible(step)) continue;
        for (var j = 0; j < step.choices.length; j++) {
          var c = step.choices[j];
          if (c.addon && c.norm === this.state[step.key]) return c;
        }
      }
      return null;
    },

    /* Every disqualifying answer currently chosen, in the order the questions ask them.
       An answer only counts while the question that holds it is on the shopper's branch:
       change fabric and a disqualifier sitting in a colour question they can no longer
       reach stops applying, the same way its answer stops applying. */
    disqualifiers: function () {
      var hits = [];
      for (var i = 0; i < this.steps.length; i++) {
        var step = this.steps[i];
        if (!step.key || !this.eligible(step)) continue;
        for (var j = 0; j < step.choices.length; j++) {
          var c = step.choices[j];
          if (c.disqualify && c.norm === this.state[step.key]) hits.push(c);
        }
      }
      return hits;
    },

    /* THE ROUTING RULE. See the header.

       "auto" is what almost every disqualifier should carry, and it defers to who the
       shopper is. An explicit "quote" or "box" on a choice overrides that, for the case
       where an answer is knowably one or the other whoever gives it -- and an explicit
       one wins over an auto, so adding a hard "quote" answer cannot be softened by an
       unrelated auto elsewhere in the form. */
    path: function () {
      var hits = this.disqualifiers();
      if (!hits.length) return 'instant';
      var routed = null;
      for (var i = 0; i < hits.length; i++) {
        var d = norm(hits[i].disqualify);
        if (d === 'quote' || d === 'box') routed = d;
      }
      if (routed) return routed;
      return this.matches(this.cfg.boxWhen || 'mode=repl') ? 'box' : 'quote';
    },

    // Why the shopper is off the instant path, in their own words. It rides to the
    // inbox so AJ opens the email already knowing which answer needs a human.
    quoteReason: function () {
      var hits = this.disqualifiers();
      var out = [];
      for (var i = 0; i < hits.length; i++) out.push(hits[i].label);
      return out.join('; ');
    },


    scrollTo: function (selector) {
      if (!selector) return;
      var target = document.querySelector(selector);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    /* "Send my quote request" does not submit anything -- the shopper still has to say
       who they are. It takes them to the form, which is already open, a screen or two up
       inside the question they were on. Falling back to the old scroll-to-a-section
       behaviour only when the section has no panel in it. */
    /* Only reached from a button pointing at #sc-quote. The page does not have one any
       more -- the form opens in the price panel and its own submit is the CTA, so a button
       that scrolled to it would be scrolling to itself. Kept because the marker contract
       still documents #sc-quote, and a section built without part: 'quote' still needs
       somewhere to send people. */
    showQuote: function () {
      if (!this.quotePanel) return this.scrollTo(this.cfg.quoteTarget);
      this.quotePanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var fields = this.quotePanel.querySelectorAll('input:not([type="hidden"]), select, textarea');
      for (var i = 0; i < fields.length; i++) {
        if (!fields[i].closest('.hidden')) {
          fields[i].focus({ preventScroll: true });
          return;
        }
      }
    },

    /* --------------------------- answering -------------------------- */

    answer: function (c) {
      if (c.action === 'scroll') {
        this.scrollTo(c.target);
        return;
      }
      var step = c.step;
      if (!step.key) return;

      this.state[step.key] = c.norm;
      this.labels[step.key] = c.label;

      /* Changing fabric or spacing can invalidate a size that is already chosen. Checked
         against the plain cover, because that is what the size dropdown is built from and
         the twin offers exactly the same combinations.

         Never against the size question itself. "My size is not on the list" is an answer
         TO that question and resolves to no variant by design, so running it through this
         guard would clear it the instant it was given and the answer would not stick. */
      var sizeKey = this.roleKey.size;
      if (sizeKey && step.key !== sizeKey && this.state[sizeKey] && !this.lookup()) {
        this.state[sizeKey] = null;
        // The label goes with it. It is what the quote form posts and what the summary
        // line reads, so leaving it behind sends AJ a size nobody chose.
        this.labels[sizeKey] = '';
      }

      this.openAll = false;
      this.render();
      this.advance(step);
    },

    // Scroll to whatever question opened as a result of the last answer.
    advance: function (from) {
      if (this.designMode || this.keyMove) return;
      var next = this.currentStep;
      if (!next || next === from) return;
      next.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var focusTarget = next.el.querySelector('h1, h2, h3, h4, h5, h6, select');
      if (focusTarget) {
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
      }
    },

    /* A step is in one of three states, not two, and the difference matters twice.

         eligible   every predicate holds. Show it, count it.
         pending    a predicate names a key nothing has answered yet. Do not show it --
                    but it is still a question this shopper is going to be asked, so it
                    is still in the count.
         excluded   a predicate names an answered key and does not hold. This branch is
                    not theirs. Not shown, not counted.

       Treating an unanswered key as "pending" rather than as the empty string is what
       makes != safe. '' != 'rectangle' is true, so under the old two-state test every
       step gated on a shape the shopper had not picked yet was eligible, and a ?variant=
       deep link -- which opens every eligible step at once -- showed both sides of the
       branch. Not knowing yet is not the same as knowing it is not that. */
    evaluate: function (step) {
      if (!step.requires) return 'eligible';
      var clauses = step.requires.split(',');
      var pending = false;
      for (var i = 0; i < clauses.length; i++) {
        var m = clauses[i].match(/^\s*([a-z0-9_-]+)\s*(!?=)\s*(.*?)\s*$/i);
        if (!m) continue;
        var raw = this.state[m[1]];
        if (raw == null || raw === '') {
          pending = true;
          continue;
        }
        var actual = norm(raw);
        var want = norm(m[3]);
        if (m[2] === '=' ? actual !== want : actual === want) return 'excluded';
      }
      return pending ? 'pending' : 'eligible';
    },

    eligible: function (step) {
      return this.evaluate(step) === 'eligible';
    },

    // One predicate, evaluated against state, in the same shape a step's requires= uses.
    // Shared so data-box-when and requires= can never drift apart.
    matches: function (expr) {
      return this.evaluate({ requires: expr }) === 'eligible';
    },

    answered: function (step) {
      return !!(step.key && this.state[step.key]);
    },

    /* ---------------------------- render ---------------------------- */

    render: function () {
      var i;
      var open = null;

      /* Before anything reads state, make sure state is answerable. Changing an early
         answer can swap which question owns a later key, and the value left behind may
         not be one the new question offers. */
      this.pruneOrphans();

      /* Which exit the answers point at, worked out before anything is asked about
         eligibility -- steps and form fields gate on requires="path=quote" exactly like
         they gate on an answer, so it has to be in state first. Computed after the prune,
         because a disqualifying answer that just got dropped must not still be routing. */
      this.state.path = this.path();
      this.labels.path = this.state.path;
      this.labels.reason = this.quoteReason();

      /* The questions this shopper is actually going to be asked, deduplicated by key.
         Two steps that write the same key are two framings of one question -- the new-pool
         and replacement spacing screens, the three colour screens -- so they count once.

         Pending counts, excluded does not. That is what keeps the number still: at the
         top of the form spacing has not been branched yet, but the shopper is going to be
         asked it, so "of 6" is true from the first screen. It only moves when a branch
         genuinely removes a question. */
      this.visibleKeys = [];
      for (i = 0; i < this.steps.length; i++) {
        var s = this.steps[i];
        if (!s.key || this.evaluate(s) === 'excluded') continue;
        if (this.visibleKeys.indexOf(s.key) === -1) this.visibleKeys.push(s.key);
      }

      // Steps reveal one at a time. In the theme editor every step stays on screen,
      // otherwise half the section would be uneditable.
      var unlocked = true;
      for (i = 0; i < this.steps.length; i++) {
        var step = this.steps[i];
        var isEligible = this.eligible(step);
        // A step the merchandiser is editing opens regardless of its condition. Without
        // that, a branch gated on an answer (question 2 is gated on question 1) can never
        // be reached in the theme editor, and reads as a missing question.
        var show = step.id === this.forcedStep || (isEligible && (this.openAll || unlocked));
        step.el.classList.toggle('hidden', !show);
        if (isEligible && !this.answered(step)) {
          if (!open) open = step;
          unlocked = false;
        }
        this.paintChoices(step);
      }
      this.currentStep = open;

      this.paintConditionals();

      if (this.sizeSelect) this.fillSizes();

      var base = this.lookup();
      var variant = this.sellable();
      var resolvable = this.allOptionsAnswered();
      var offPath = this.state.path !== 'instant';
      // Off the instant path there is nothing to sell whatever the variants say. On it,
      // a combination that resolves to nothing is the old fallback: not manufactured.
      var quoteOnly = offPath || (resolvable && !variant);
      var buyable = !offPath && !!variant && variant.available !== false;

      /* The form waits for the questions to finish.

         An answer can put a shopper on the quote route as early as question 3 -- a swimout
         does it -- and opening a twelve-field form beside question 4 buries the thing they
         are actually being asked. The panel keeps quoting the route instead, and turns
         into the form once there is nothing left to answer. `open` is the first
         unanswered question, so a null one means they are done. */
      var formOpen = this.posted || (quoteOnly && !open);

      this.setTokens(variant, quoteOnly, open, formOpen);
      this.setCtas(buyable, quoteOnly);
      this.syncQuotePanel(formOpen);

      // The no-JS picker belongs to the plain cover, so it can only ever hold a plain
      // cover variant. It is kept pointed at the equivalent one so that if this script
      // ever stops driving the page mid-session, what is left behind is coherent.
      if (this.nativeSelect && base) {
        this.nativeSelect.value = String(base.id);
        this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.setUrlVariant(variant ? variant.id : null);

      /* Anything above can have changed the pinned column's height -- a price line, the
         payment terms, the quote form opening -- and its height is what its travel is
         measured against. ResizeObserver catches this on its own where there is one;
         this is for where there is not. */
      this.schedulePin();
    },

    /* THE INLINE EXIT.

       The quote form is not a place the shopper is sent, it is a thing that opens in the
       question they were already looking at. So there is nothing here but: show it when
       the answers stop being sellable, and pour the answers into it.

       Every field carrying data-sc-fill is one the questions might already have answered.
       If they have, it is filled and folded away; if they have not, it stays visible for
       the shopper to fill in. That is the whole mechanism, and it is why adding the shape
       question as Q1 later removes the shape field from this form on its own. */
    syncQuotePanel: function (open) {
      var panel = this.quotePanel;
      if (!panel) return;

      /* Every route has a reason, including the ones nobody chose. A combination that is
         simply not built still has to arrive in the inbox saying so. */
      this.labels.reason = this.quoteReason() ||
        (open && !this.posted ? 'That combination is not available to buy online' : '');
      /* Opening this is the largest single change in the pinned column's height, which
         render() hands to the pin. Nothing here caps or un-sticks that column -- see
         THE PINNED COLUMN.

         The scroll is on the edge, not on the state: render() runs on every answer, and a
         page that re-scrolled itself each time a shopper changed their mind about a colour
         would be unusable. `sc-ready` keeps it off the first render too -- a shopper coming
         back from a posted form arrives with the panel already open, and the constructor
         has its own, better scroll for that one. */
      var was = !panel.classList.contains('hidden');
      panel.classList.toggle('hidden', !open);
      if (open && !was && this.root.classList.contains('sc-ready')) this.revealColumn();

      var fields = panel.querySelectorAll('[data-sc-fill]');
      for (var i = 0; i < fields.length; i++) {
        var el = fields[i];
        var value = this.fillValue(el.getAttribute('data-sc-fill'));
        var isInput = el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA';
        var input = isInput ? el : el.querySelector('input, select, textarea');
        if (!input) continue;

        if (isInput && input.type === 'hidden') {
          // A disabled input is not posted at all, which is how a blank answer stays out
          // of the email rather than arriving as an empty line.
          input.value = value;
          input.disabled = !value;
          continue;
        }

        /* A visible field. Hiding one that is `required` and empty would wedge the form
           on a validation error the shopper cannot see or reach, so it is only ever
           folded away once the field has actually taken the value. */
        var known = !!value && this.setFieldValue(input, value);
        el.classList.toggle('hidden', known);
      }

      this.renderBuild(panel);
    },

    /* WHAT THEY PICKED, READ BACK.

       The quote route replaces [sc:spec] with copy about being priced by hand, so by the
       time this panel opens nothing on screen still says what the shopper chose. They are
       about to ask a human for a price on it, and AJ is about to quote from it, so both
       of them get it in full: a list they can check before sending, and the same lines in
       one long-text field that posts with the form.

       It is built from the questions themselves, not a fixed list of fields, so a question
       added to the section appears here on its own -- which is the same promise
       data-sc-fill makes for the fields below it. */
    renderBuild: function (panel) {
      var wrap = panel.querySelector('[data-sc-build]');
      if (!wrap) return;
      var list = wrap.querySelector('[data-sc-build-list]') || wrap;
      var lines = this.buildLines();

      wrap.classList.toggle('hidden', lines.length === 0);
      list.textContent = '';
      for (var i = 0; i < lines.length; i++) {
        var item = document.createElement('li');
        var name = document.createElement('span');
        name.className = 'sc-build__name';
        name.textContent = lines[i].name + ': ';
        item.className = 'sc-build__item';
        item.appendChild(name);
        item.appendChild(document.createTextNode(lines[i].value));
        list.appendChild(item);
      }
    },

    // Every answered question, in the order it was asked, as { name, value }. Only the
    // questions this shopper actually gets -- a branch that is not theirs is not part of
    // what they built.
    buildLines: function () {
      var order = this.visibleKeys || this.keyOrder;
      var lines = [];
      for (var i = 0; i < order.length; i++) {
        var value = this.labels[order[i]];
        if (!value) continue;
        lines.push({ name: this.keyName(order[i]), value: value });
      }
      return lines;
    },

    // The same lines, one per row, for the long-text field that posts with the form.
    buildText: function () {
      var lines = this.buildLines();
      var out = [];
      for (var i = 0; i < lines.length; i++) out.push(lines[i].name + ': ' + lines[i].value);
      return out.join('\n');
    },

    /* What a question is called when its answer is read back. summary= on the step
       marker, else the line item property name it already posts under, else the question's
       own heading -- which is why two framings of one question (the two spacing screens,
       the three colour screens) want an explicit summary=: their headings differ and the
       answer is the same answer. The eligible framing wins; an ineligible one is only a
       fallback for a key nobody is being asked any more. */
    keyName: function (key) {
      var fallback = '';
      for (var i = 0; i < this.steps.length; i++) {
        var step = this.steps[i];
        if (step.key !== key) continue;
        var name = step.summary || step.property || headingText(step.el) || key;
        if (this.eligible(step)) return name;
        if (!fallback) fallback = name;
      }
      return fallback || key;
    },

    // Most fills are the label of an answer. Three are not: the one-line summary, the
    // itemised build and the reason, which are assembled rather than chosen.
    fillValue: function (key) {
      if (key === 'spec') return this.summaryLine();
      if (key === 'build') return this.buildText();
      return this.labels[key] || '';
    },

    /* A <select> can only hold a value it has an option for.

       The return value matters: a field that could not take its value has NOT been
       answered, whatever state says, and folding it away would leave a `required` field
       hidden and empty -- the form would refuse to submit on a validation error the
       shopper can neither see nor reach. So an unmatched value leaves the field visible
       and asked. That is what happens when the shape question is answered "every other
       shape": the question narrowed it down, and the form still has to ask which. */
    setFieldValue: function (input, value) {
      if (input.tagName !== 'SELECT') {
        input.value = value;
        return true;
      }
      for (var i = 0; i < input.options.length; i++) {
        if (input.options[i].value === value) {
          input.value = value;
          return true;
        }
      }
      return false;
    },

    // What the shopper built, in one line, whether or not it resolved to a variant.
    // The price panel's [sc:spec] can only speak when there is a variant; this can
    // always speak, which is what the quote email needs.
    summaryLine: function () {
      var parts = [];
      for (var i = 0; i < this.keyOrder.length; i++) {
        var label = this.labels[this.keyOrder[i]];
        if (label) parts.push(label);
      }
      return parts.join(' / ');
    },

    /* Content that belongs to one route. Evaluated after state.path is worked out, so a
       column can gate on requires="path=instant" exactly like a question gates on an
       answer.

       Everything stays visible in the theme editor. A merchandiser cannot edit copy that
       is only on screen for a shopper who answered three questions a particular way, and
       a blank column reads as a missing block rather than a conditional one. */
    /* AN ANSWER HAS TO BE ONE THE QUESTION ON SCREEN ACTUALLY OFFERS.

       Two steps can write the same key -- the two spacing screens, the three colour
       screens -- and which of them is eligible depends on an earlier answer. Go back and
       change that earlier answer and the key survives into a question that may not offer
       it.

       The case that bit: answer "not sure, the anchors are uneven" on the replacement
       spacing screen, which stores grid="unsure" and routes to the Perfect Fit Box, then
       go back to question 1 and switch to "New pool". The new-pool spacing screen offers
       only 5x5 and 3x3, so nothing shows as selected -- but `answered()` only asks whether
       the key is truthy, so the form counted it as answered and unlocked every question
       after it. The shopper ended up past a question that looked blank, carrying an
       "Anchor spacing: not sure, the anchors are uneven" that would have ridden all the
       way to the order on a pool with no anchors in the deck yet, with the price panel
       showing a placeholder and no explanation of why.

       Dropping the value puts the question back in front of them, which is the only
       honest thing to do: nobody ever answered THIS question.

       Deliberately narrow. It clears a value only when the eligible step for that key
       offers a fixed set of answers and none of them match:
         - a step whose answers come from the size dropdown is skipped, because its
           answers are built at runtime from the variants, and it has its own invalidation
           in answer() already;
         - exits are skipped, because choosing one never stored anything;
         - a key whose step is not eligible is left alone -- that is a branch nobody is
           on, not a wrong answer. */
    pruneOrphans: function () {
      for (var i = 0; i < this.steps.length; i++) {
        var step = this.steps[i];
        if (!step.key || !this.eligible(step)) continue;
        var value = this.state[step.key];
        if (!value) continue;

        var runtimeAnswers = false;
        var matched = false;
        for (var j = 0; j < step.choices.length; j++) {
          var c = step.choices[j];
          if (c.control === 'size') runtimeAnswers = true;
          else if (c.action !== 'scroll' && c.norm === value) matched = true;
        }
        if (runtimeAnswers || matched) continue;

        this.state[step.key] = null;
        this.labels[step.key] = '';
      }
    },

    paintConditionals: function () {
      for (var i = 0; i < this.conditionals.length; i++) {
        var c = this.conditionals[i];
        var show = this.designMode || this.evaluate(c) === 'eligible';
        c.el.classList.toggle('hidden', !show);
      }
    },

    paintChoices: function (step) {
      var current = step.key ? this.state[step.key] : null;
      var group = this.radios(step);
      var checked = null;
      for (var i = 0; i < group.length; i++) {
        var c = group[i];
        var on = current != null && c.norm === current;
        c.el.classList.toggle('is-sc-selected', on);
        c.el.setAttribute('aria-checked', on ? 'true' : 'false');
        c.el.setAttribute('tabindex', '-1');
        if (on) checked = c;
      }
      var stop = checked || group[0];
      // Roving tabindex. A radio group is a single tab stop: Tab lands on the answer
      // already chosen, or on the first one when nothing is chosen yet, and the arrow
      // keys move between them from there.
      if (stop) stop.el.setAttribute('tabindex', '0');
    },

    allOptionsAnswered: function () {
      for (var role in this.roleKey) {
        if (!Object.prototype.hasOwnProperty.call(this.roleKey, role)) continue;
        if (!this.state[this.roleKey[role]]) return false;
      }
      return true;
    },

    lookup: function (index) {
      var fab = this.state[this.roleKey.fabric];
      var grid = this.state[this.roleKey.spacing];
      var size = this.state[this.roleKey.size];
      if (!fab || !grid || !size) return null;
      var from = index || this.index;
      return (from[fab] && from[fab][grid] && from[fab][grid][size]) || null;
    },

    /* The variant that would actually be sold, out of whichever of the two products the
       answers point at. This is the only thing that ever reaches the cart or a price.

       A cutout answer with no twin variant behind it returns null, which reads exactly
       like a combination that is not manufactured: no price, no Add to cart, the quote
       button instead. That is the safe direction -- the alternative is selling a plain
       cover to somebody who asked for a cutout. */
    sellable: function () {
      if (!this.addonWanted()) return this.lookup();
      return this.lookup(this.addonIndex);
    },

    /* Only sizes this fabric and spacing can be built in, price shown inline.

       Which sizes EXIST comes off the plain cover, always: it is the canonical list, and
       the twin is the same 130 combinations. Which price is shown against them comes off
       whichever product the Steps answer points at, so the dropdown and [sc:price] can
       never disagree about what the shopper is being charged.

       If a cutout answer is chosen and the twin has no variant for a size, that size is
       still offered with no price on it. Picking it lands on the quote form, which is
       what [sc:price] says too. Hiding it instead would make sizes appear and disappear
       as the Steps answer changed. */
    fillSizes: function () {
      var fab = this.state[this.roleKey.fabric];
      var grid = this.state[this.roleKey.spacing];
      var current = this.state[this.roleKey.size];
      var priced = this.addonWanted() ? this.addonIndex : this.index;

      this.sizeSelect.innerHTML = '';
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = this.cfg.sizePlaceholder || 'Choose your pool size';
      this.sizeSelect.appendChild(placeholder);
      this.sizeSelect.disabled = !(fab && grid);
      if (!fab || !grid) return;

      var reachable = false;
      var offered = 0;
      for (var i = 0; i < this.sizeOrder.length; i++) {
        var key = this.sizeOrder[i];
        var v = this.index[fab] && this.index[fab][grid] && this.index[fab][grid][key];
        if (!v) continue; // not manufactured in this combination, so never offered
        var sold = (priced[fab] && priced[fab][grid] && priced[fab][grid][key]) || null;
        var opt = document.createElement('option');
        opt.value = key;
        opt.textContent = sold ? v.size + '   ' + sold.price : v.size;
        opt.dataset.label = v.size;
        if ((sold || v).available === false) opt.disabled = true;
        this.sizeSelect.appendChild(opt);
        offered++;
        if (key === current) reachable = true;
      }
      /* An answer can put the spacing beyond what the variants cover -- "not sure, the
         anchors are uneven" is one, and the shape question will add more. The dropdown
         then has a placeholder and nothing else, and a select the shopper can open onto
         an empty list reads as broken rather than as not applicable. */
      if (!offered) this.sizeSelect.disabled = true;
      this.sizeSelect.value = reachable ? current : '';
    },

    /* TOKEN NAMES
         [sc:price]      price, or the placeholder before it can be known
         [sc:spec]       "Royal Mesh, 5x5 spacing, 20' x 40' pool"
         [sc:anchors]    anchor estimate line
         [sc:fabric] [sc:spacing] [sc:size]   individual answers
         [sc:helper]     guidance for the question currently on screen
         [sc:step-index] [sc:step-total]      numbering, per step, branch aware
         [sc:answer-KEY] the label of the answer stored under any state key            */
    setTokens: function (variant, quoteOnly, open, formOpen) {
      var cfg = this.cfg;
      var values = {};

      // variant is whatever is actually being sold, out of either product. Both carry
      // the same option values, so the spec and anchor lines read the same off either.
      var withAddon = this.addonWanted();
      /* `quoteOnly` beats a variant that happens to resolve. Off the instant path the
         three option answers can still name a real, manufactured variant -- a swimout on
         an ordinary Royal Mesh 20x40 does exactly that -- and reading its price back
         would put a firm number and a "Royal Mesh in Green, 5x5 spacing" spec line
         directly above a form asking for a quote. Add to cart is already hidden by then;
         this is the panel agreeing with it. */
      if (variant && !quoteOnly) {
        values.price = variant.price;
        values.addon = withAddon ? this.cfg.addonNote || '' : '';
        // Colour is not on the variant -- it rides as a line item property -- but it is
        // part of what the shopper thinks they are buying, so the summary reads it back
        // with the rest. The key is named here rather than discovered, the same way the
        // three option roles are: "Royal Mesh in Green, 5x5 spacing, 20' x 40' pool".
        // Drop the colour question and this quietly goes back to the three-part line.
        var color = this.labels.color;
        values.spec = variant.fabric + (color ? ' in ' + color : '') +
          ', ' + variant.spacing + ' spacing, ' + variant.size + ' pool';
        values.fabric = variant.fabric;
        values.spacing = variant.spacing;
        values.size = variant.size;
        // The anchor estimate is perimeter over spacing, so it is only true of a pool we
        // have real dimensions for. Off the instant path the size is a rough figure or
        // absent, and an estimate drawn from it would read as a promise.
        values.anchors = this.state.path === 'instant' ? this.anchorLine(variant) : '';
      } else if (quoteOnly) {
        // The Perfect Fit Box gets its own two lines when the section supplies them,
        // because "send us the old one" is a different promise from "we will price it".
        var box = this.state.path === 'box';
        var routeSpec = (box && cfg.boxSpec) || cfg.quoteSpec || '';
        values.price = (box && cfg.boxPrice) || cfg.quotePrice || '';
        /* Those lines point at a form ("fill in the pool details here"), so they can only
           be true once the form is on screen. Until then the panel says what is coming. */
        values.spec = formOpen ? routeSpec : (cfg.pendingSpec || routeSpec);
        values.anchors = '';
        values.addon = '';
      } else {
        values.price = cfg.pricePlaceholder || '';
        values.spec = cfg.specPlaceholder || '';
        values.anchors = '';
        values.addon = '';
      }
      if (values.fabric == null) values.fabric = this.labels[this.roleKey.fabric] || '';
      if (values.spacing == null) values.spacing = this.labels[this.roleKey.spacing] || '';
      if (values.size == null) values.size = this.labels[this.roleKey.size] || '';

      values.helper = open ? open.helper || cfg.helperStart || '' : cfg.helperDone || '';

      var total = (this.visibleKeys || this.keyOrder).length;
      for (var i = 0; i < this.tokens.length; i++) {
        var span = this.tokens[i];
        var name = span.getAttribute('data-sc-token');
        var out;
        if (name === 'step-total') {
          out = String(total);
        } else if (name === 'step-index') {
          out = String(this.stepNumber(span));
        } else if (name.indexOf('answer-') === 0) {
          out = this.labels[name.slice(7)] || '';
        } else {
          out = values[name];
        }
        span.textContent = out == null ? '' : out;
      }
    },

    /* Steps that answer the same key are two framings of one question, so they share a
       number -- that is what keeps both spacing screens reading 02.

       Numbered against the questions THIS shopper gets, not against every question in the
       section. Counting all of them was right while every shopper answered all six; it
       stops being right the moment a branch removes one, and reading "Question 4 of 9"
       on a form with five screens left is worse than no counter at all. */
    stepNumber: function (node) {
      var row = node.closest('.sc-step');
      if (!row) return '';
      var id = row.getAttribute('data-sc-step');
      var order = this.visibleKeys || this.keyOrder;
      for (var i = 0; i < this.steps.length; i++) {
        if (this.steps[i].id !== id) continue;
        var pos = order.indexOf(this.steps[i].key);
        return pos === -1 ? '' : pos + 1;
      }
      return '';
    },

    // Cover perimeter over spacing, rounded up. Labelled an estimate on the page and
    // confirmed on the signoff drawing.
    anchorLine: function (variant) {
      var pool = dims(variant.size);
      var space = dims(variant.spacing);
      if (!pool || !space) return '';
      var perimeter = 2 * (pool[0] + 2 + (pool[1] + 2));
      var count = Math.ceil(perimeter / space[0]);
      return (this.cfg.anchorTemplate || 'Estimated anchors: about [count].').replace('[count]', count);
    },

    setCtas: function (buyable, quoteOnly) {
      this.addDisabled = !buyable;
      if (this.addCta) {
        // .button[aria-disabled='true'] is already dimmed and cursor: not-allowed by the
        // theme, so the attribute is the whole treatment. submit() refuses the click.
        this.addCta.classList.toggle('hidden', quoteOnly);
        this.addCta.setAttribute('aria-disabled', buyable ? 'false' : 'true');
      }
      if (this.quoteCta) this.quoteCta.classList.toggle('hidden', !quoteOnly);
      if (this.terms) this.terms.classList.toggle('hidden', !buyable);
      if (this.sticky) this.sticky.classList.toggle('hidden', !buyable);
    },

    /* --------------------------- deep links -------------------------- */

    setUrlVariant: function (id) {
      if (!window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      if (String(url.searchParams.get('variant') || '') === String(id || '')) return;
      if (id) url.searchParams.set('variant', id);
      else url.searchParams.delete('variant');
      window.history.replaceState({}, '', url.toString());
    },

    restoreFromUrl: function () {
      var id = new URL(window.location.href).searchParams.get('variant');
      var variant = id && this.byId[String(id)];
      if (!variant) return;
      if (this.roleKey.fabric) {
        this.state[this.roleKey.fabric] = variant.fabKey;
        this.labels[this.roleKey.fabric] = variant.fabric;
      }
      if (this.roleKey.spacing) {
        this.state[this.roleKey.spacing] = variant.gridKey;
        this.labels[this.roleKey.spacing] = variant.spacing;
      }
      if (this.roleKey.size) {
        this.state[this.roleKey.size] = variant.sizeKey;
        this.labels[this.roleKey.size] = variant.size;
      }
      /* The link cannot carry the answers that are not variant options, so leave every
         question on screen for the shopper to finish. That includes the Steps answer, so
         a link to a with-cutout variant restores the fabric, spacing and size it names
         but not the cutout itself -- the shopper re-answers Steps, and the URL is then
         rewritten to whichever variant that lands on. */
      this.openAll = true;
    }
  };

  /* ------------------------------------------------------------------ */

  function boot() {
    var engines = document.querySelectorAll('[data-sc-engine]');
    for (var i = 0; i < engines.length; i++) {
      if (engines[i].scBuilder) continue;
      try {
        engines[i].scBuilder = new Builder(engines[i]);
      } catch (e) {
        // Every bail-out leaves the no-JS fallback in place: the native variant picker
        // stays visible and the product is still buyable. Say which failure it was.
        warn(e.message);
      }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
